import React, {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {ProgressSpinner} from "primereact/progressspinner";
import {useDispatch, useSelector} from "react-redux";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import {getStorage} from "../../../system/storage";
import UpdateShareToChat from "../../goals/components/update-share-chat";
import {formatPersianDate} from "../../../models/goal-update/goal-update-api";
import {MILESTONE_RESULTS, OFF_TRACK_PATHS, PROJECT_STATES, TASK_ACTIONS, projectUpdateApi} from "../../../models/project-update/project-update-api";
import {WidgetCard} from "./project-update-widgets";
import "../../goals/components/goal-updates.css";
import "./project-updates.css";
import {RichTextView, richTextToPlain} from "../../../system/components/rich-text";

// «زوفینو | گزارش بروزرسانی» (design مودال نمایش بروزرسانی): one published project update with its snapshot charts.
// Opened from the project's history and from notifications (?projectUpdate=<id>). `update` shows a given entry
// (an old timeline item); `updateId` loads one.

export function ProjectStateChip({state}) {
    const config = PROJECT_STATES[state];
    if (!config) return null;
    const tone = config.tone === "green" ? "teal" : config.tone;
    return <span className={`gu-chip gu-tone-${tone}`}><span className="gu-chip__dot"/>{config.label}</span>;
}

const labelOf = (options, value) => options.find((option) => option.value === value)?.label || value || "—";

function Section({icon, title, color = "#e6531e", children, className = "", style}) {
    return (
        <section className={`gu-card p-4 ${className}`} style={style}>
            <h3 className="m-0 mb-3 flex align-items-center gap-2 text-base font-bold" style={{color}}><Icon icon={`mi mi:${icon}`} className="text-xl"/>{title}</h3>
            {children}
        </section>
    );
}

function Pairs({rows}) {
    return (
        <div className="grid m-0">
            {rows.filter((row) => row.value).map((row) => (
                <div key={row.label} className="col-12 md:col-6 p-1">
                    <div className="p-2 border-round-xl bg-white h-full">
                        <span className="block text-2xs text-500 mb-1">{row.label}</span>
                        <RichTextView className="text-sm" value={row.value}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ProjectUpdateViewDialog({updateId, update: givenUpdate, project, visible, onHide}) {
    const dispatch = useDispatch();
    const memberships = useSelector((state) => state.app.workspaceMembershipData?.workspace?.workspace_memberships) || [];
    const [update, setUpdate] = useState(givenUpdate || null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;
        if (givenUpdate) {
            setUpdate(givenUpdate);
            return;
        }
        if (!updateId) return;
        let cancelled = false;
        setLoading(true);
        setUpdate(null);
        projectUpdateApi.get(updateId)
            .then((data) => { if (!cancelled) setUpdate(data); })
            .catch(() => {
                if (cancelled) return;
                dispatch(setMessage({severity: "warn", content: "این بروزرسانی پیدا نشد یا به آن دسترسی ندارید."}));
                onHide?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // onHide is a callback prop with an unstable identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, updateId, givenUpdate]);

    const projectData = update?.project || project;
    const saved = Boolean(update?.documentId);
    const author = update?.author?.member;
    const personName = (key) => memberships.find((item) => item.documentId === key || String(item.id) === String(key))?.member?.fullname || "";
    const snapshot = update?.metricsSnapshot;
    const risk = update?.stateDetails?.risk;
    const offTrack = update?.stateDetails?.offTrack;
    const closure = update?.closure;
    const closed = update && !PROJECT_STATES[update.selectedState]?.open;
    const link = projectData?.documentId && update?.documentId
        ? `${window.location.origin}${window.location.pathname}#/projects/${projectData.documentId}?projectUpdate=${update.documentId}&ws=${getStorage("workspace") || ""}`
        : window.location.href;

    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({title: projectData?.title || "بروزرسانی پروژه", url: link});
                return;
            }
            await navigator.clipboard.writeText(link);
            dispatch(setMessage({severity: "success", content: "لینک بروزرسانی کپی شد."}));
        } catch (error) {
            if (error?.name !== "AbortError") dispatch(setMessage({severity: "warn", content: "کپی لینک انجام نشد."}));
        }
    };

    const print = () => {
        document.body.classList.add("gu-printing");
        const done = () => {
            document.body.classList.remove("gu-printing");
            window.removeEventListener("afterprint", done);
        };
        window.addEventListener("afterprint", done);
        window.print();
        setTimeout(done, 1000);
    };

    const footer = (
        <div className="gu flex align-items-center justify-content-between gap-2 gu-no-print">
            <div className="flex gap-2">
                <button type="button" className="gu-soft-button gu-soft-button--primary" onClick={share}><Icon icon="mi mi:share" className="text-lg"/>اشتراک‌گذاری</button>
                <button type="button" className="gu-soft-button gu-soft-button--gray" onClick={print}><Icon icon="mi mi:print" className="text-lg"/>چاپ گزارش</button>
            </div>
            <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={onHide}>بستن</button>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={<span className="text-lg font-bold" style={{color: "#e6531e"}}>زوفینو | گزارش بروزرسانی</span>}
            footer={footer}
            className="gu-dialog rtl"
            dismissableMask
            draggable={false}
            blockScroll
        >
            <div className="gu gu-print shabnam-font" dir="rtl">
                {loading || !update ? (
                    <div className="flex justify-content-center p-6"><ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/></div>
                ) : (
                    <div className="flex flex-column gap-4">
                        <div className="flex flex-column md:flex-row md:align-items-start justify-content-between gap-3">
                            <div className="flex flex-column gap-2">
                                <h2 className="m-0 text-3xl font-bold">{projectData?.title || ""}</h2>
                                <div className="flex flex-wrap align-items-center gap-3">
                                    <ProjectStateChip state={update.selectedState}/>
                                    <span className="text-sm gu-muted">آخرین بروزرسانی: {formatPersianDate(update.publishedAt || update.createdAt)}</span>
                                    {update.progressSnapshot !== null && update.progressSnapshot !== undefined && <span className="text-sm gu-muted">پیشرفت: {Number(update.progressSnapshot).toLocaleString("fa-IR")}٪</span>}
                                    {update.previousState && update.previousState !== update.selectedState && PROJECT_STATES[update.previousState] && (
                                        <span className="text-sm gu-muted">قبلی: {PROJECT_STATES[update.previousState].label}</span>
                                    )}
                                </div>
                            </div>
                            {author && (
                                <div className="gu-card flex align-items-center gap-3 px-3 py-2" style={{background: "#fcf9f8"}}>
                                    <Avatar memberData={author} style={{width: "3rem", height: "3rem"}}/>
                                    <div className="flex flex-column gap-1">
                                        <strong className="text-base">{author.fullname}</strong>
                                        <span className="text-xs gu-muted">ثبت‌کننده بروزرسانی</span>
                                        <span className="text-xs gu-muted flex align-items-center gap-1"><Icon icon="mi mi:schedule" className="text-sm"/>{formatPersianDate(update.publishedAt || update.createdAt, true)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Section icon="article" title="خلاصه وضعیت">
                            <RichTextView className="m-0 text-sm" value={update.summary || update.briff} empty={<p className="m-0 text-sm gu-muted">خلاصه‌ای ثبت نشده است.</p>}/>
                        </Section>

                        {snapshot && update.widgets?.length > 0 && (
                            <Section icon="bar_chart" title="نمودارهای پیشرفت" color="#181311">
                                <p className="m-0 mb-3 text-2xs gu-muted">داده‌ها مربوط به لحظه انتشار ({formatPersianDate(snapshot.generatedAt, true)}) است.</p>
                                <div className="grid m-0">
                                    {update.widgets.map((type) => (
                                        <div key={type} className="col-12 md:col-6 p-1"><WidgetCard type={type} metrics={snapshot} compact/></div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {update.selectedState === "has-risks" && risk?.description && (
                            <Section icon="warning" title="ریسک و اقدام اصلاحی" color="#b45309" style={{background: "#fffbeb", borderColor: "#fde68a"}}>
                                <Pairs rows={[
                                    {label: "شرح ریسک", value: risk.description},
                                    {label: "اثر احتمالی", value: risk.impact},
                                    {label: "مالک پیگیری", value: personName(risk.owner)},
                                    {label: "اقدام اصلاحی", value: risk.action},
                                    {label: "موعد بررسی مجدد", value: formatPersianDate(risk.reviewAt)},
                                ]}/>
                            </Section>
                        )}
                        {update.selectedState === "out-of-way" && offTrack?.cause && (
                            <Section icon="error" title="خارج از مسیر و تصمیم اصلاحی" color="#b91c1c" style={{background: "#fef2f2", borderColor: "#fecaca"}}>
                                <Pairs rows={[
                                    {label: "علت", value: offTrack.cause},
                                    {label: "اثر روی خروجی پروژه", value: offTrack.projectImpact},
                                    {label: "اثر روی هدف متصل", value: offTrack.goalImpact},
                                    {label: "تصمیم پیشنهادی", value: offTrack.decision},
                                    {label: "مسیر اصلاحی", value: labelOf(OFF_TRACK_PATHS, offTrack.path)},
                                    {label: "مالک تصمیم", value: personName(offTrack.decisionOwner)},
                                    {label: "تاریخ تصمیم‌گیری", value: formatPersianDate(offTrack.decisionAt)},
                                ]}/>
                            </Section>
                        )}
                        {update.stateDetails?.mismatchReason && (
                            <Section icon="info" title="دلیل انتخاب این وضعیت" color="#c2410c">
                                <p className="m-0 text-sm" style={{lineHeight: 2}}>{update.stateDetails.mismatchReason}</p>
                            </Section>
                        )}

                        <div className="grid m-0">
                            <div className="col-12 md:col-7 p-0 md:pl-2 mb-3 md:mb-0">
                                <Section icon="verified" title="دستاوردها" color="var(--gu-teal)" className="h-full">
                                    {update.accomplishments?.length ? (
                                        <div className="flex flex-column gap-2">
                                            {update.accomplishments.map((item, index) => (
                                                <div key={index} className="gu-list-item"><span className="inline-flex flex-none" style={{color: "var(--gu-teal)"}}><Icon icon="mi mi:check_circle" className="text-lg"/></span>{item}</div>
                                            ))}
                                        </div>
                                    ) : <span className="text-sm gu-muted">موردی ثبت نشده است.</span>}
                                </Section>
                            </div>
                            <div className="col-12 md:col-5 p-0 md:pr-2">
                                <section className="gu-card p-4 h-full" style={{background: "#fdf3f0", borderColor: "#fde0d6"}}>
                                    <h3 className="m-0 mb-3 flex align-items-center gap-2 text-base font-bold" style={{color: "#e6531e"}}><Icon icon="mi mi:warning" className="text-xl"/>موانع و چالش‌ها</h3>
                                    {update.obstacles?.length ? (
                                        <div className="flex flex-column gap-2">
                                            {update.obstacles.map((item, index) => <div key={index} className="p-3 border-round-xl text-sm bg-white" style={{color: "#c2410c", lineHeight: 1.9}}>{item}</div>)}
                                        </div>
                                    ) : <span className="text-sm gu-muted">مانعی ثبت نشده است.</span>}
                                </section>
                            </div>
                        </div>

                        {update.nextActions?.length > 0 && (
                            <Section icon="event_repeat" title="گام‌های بعدی" color="#181311">
                                <div className="grid m-0">
                                    {update.nextActions.map((item, index) => (
                                        <div key={index} className="col-12 md:col-6 p-1"><div className="gu-todo"><span className="gu-todo__box"/>{item}</div></div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {closed && closure && (
                            <Section icon="lock" title={`بستن پروژه · ${PROJECT_STATES[update.selectedState]?.label}`} color="#44403c">
                                <Pairs rows={[
                                    {label: "دستاورد نهایی", value: closure.result},
                                    {label: "شواهد تحقق", value: closure.evidence},
                                    {label: "دلیل", value: closure.reason},
                                    {label: "تصمیم‌گیرنده", value: personName(closure.decisionMaker)},
                                    {label: "آخرین وضعیت واقعی", value: closure.lastState},
                                    {label: "درس‌آموخته‌ها", value: closure.lessons},
                                ]}/>
                                {closure.tasks?.length > 0 && (
                                    <p className="m-0 mt-3 text-xs gu-muted">
                                        تعیین تکلیف کارها: {TASK_ACTIONS.map((action) => {
                                            const count = closure.tasks.filter((item) => item.action === action.value).length;
                                            return count ? `${action.label} ${count.toLocaleString("fa-IR")}` : null;
                                        }).filter(Boolean).join(" · ")}
                                    </p>
                                )}
                                {closure.milestones?.length > 0 && (
                                    <p className="m-0 mt-2 text-xs gu-muted">
                                        مایلستون‌ها: {MILESTONE_RESULTS.map((result) => {
                                            const count = closure.milestones.filter((item) => item.result === result.value).length;
                                            return count ? `${result.label} ${count.toLocaleString("fa-IR")}` : null;
                                        }).filter(Boolean).join(" · ")}
                                    </p>
                                )}
                                {closure.goals?.length > 0 && (
                                    <p className="m-0 mt-2 text-xs gu-muted">
                                        اثر بر اهداف: {closure.goals.map((item) => `${item.contribution?.toLocaleString("fa-IR") ?? 0}٪${item.requestReview ? " (بازبینی)" : ""}${item.needsReplacement ? " (نیاز به جایگزین)" : ""}`).join("، ")}
                                    </p>
                                )}
                            </Section>
                        )}

                        {saved && (
                            <UpdateShareToChat
                                card={{
                                    type: "project-update",
                                    documentId: update.documentId,
                                    title: projectData?.title || "",
                                    state: update.selectedState,
                                    stateLabel: PROJECT_STATES[update.selectedState]?.label || null,
                                    progress: update.progressSnapshot ?? null,
                                    summary: richTextToPlain(update.summary || update.briff) || null,
                                    author: author?.fullname || null,
                                    publishedAt: update.publishedAt || update.createdAt,
                                }}
                                recipients={update.recipients || []}
                                onSend={(payload) => projectUpdateApi.share(update.documentId, payload)}
                                onSent={(result) => result?.update && setUpdate(result.update)}
                            />
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
}

export default ProjectUpdateViewDialog;
